// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useRef, useState } from 'react';
import { Button, InlineNotification } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { AuthError } from '@azure/msal-browser';
import HeliosMark from '../shell/HeliosMark';
import '../../pages/Login.css';

/** Entra codes for a sign-in refused because the account is disabled (guest, member). */
const ACCOUNT_DISABLED_CODES = new Set(['AADSTS500571', 'AADSTS50057']);

const ACCOUNT_DISABLED_REASON = 'Your account has been deactivated. Contact your administrator.';
const GENERIC_REASON =
  'Something went wrong while signing you in. Try again, or contact your administrator if it keeps happening.';

function errorCode(error: unknown): string | undefined {
  // An AuthError's message is `${errorCode}: ${errorMessage}`, so this covers both.
  const message = error instanceof Error ? error.message : '';
  return (
    message.match(/AADSTS\d+/)?.[0] ?? (error instanceof AuthError ? error.errorCode : undefined)
  );
}

interface SignInErrorPageProps {
  error: unknown;
  onSignInAgain: () => void;
}

/**
 * Shown instead of the app when MSAL startup fails (e.g. Entra refuses the
 * sign-in redirect for a disabled account). Rendered outside the app's
 * providers, so it sets its own theme.
 */
function SignInErrorPage({ error, onSignInAgain }: SignInErrorPageProps) {
  const code = errorCode(error);
  const reason =
    code && ACCOUNT_DISABLED_CODES.has(code) ? ACCOUNT_DISABLED_REASON : GENERIC_REASON;
  // A second click while the first redirect is starting would fail with
  // `interaction_in_progress` and fall back to /login, racing the redirect.
  const started = useRef(false);
  const [signingIn, setSigningIn] = useState(false);
  const handleSignInAgain = () => {
    if (started.current) return;
    started.current = true;
    setSigningIn(true);
    onSignInAgain();
  };

  // `data-carbon-theme` drives Helios' own tokens; `cds--g100` drives
  // Carbon's `--cds-*` set, without which the Button renders light.
  return (
    <div data-carbon-theme="g100" className="cds--g100">
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <HeliosMark size={32} className="login-mark" />
            <h1 className="login-brand">{"We couldn't sign you in"}</h1>
          </div>
          <InlineNotification
            className="login-error"
            kind="error"
            role="alert"
            lowContrast
            hideCloseButton
            title={reason}
            subtitle={code && `Error code: ${code}`}
          />
          <Button
            className="login-button"
            renderIcon={ArrowRight}
            disabled={signingIn}
            onClick={handleSignInAgain}
          >
            Sign in again
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SignInErrorPage;
